/** 实验室租户成员登录（团队编码可选 + 账号 + 密码），供首页与 LoginModal 复用 */

export type TeamPickCandidate = {
  userId: string;
  username: string;
  teamId: string | null;
  teamCode: string;
  teamName: string;
};

export type TenantLoginSuccess = {
  ok: true;
  token: string;
  user: Record<string, unknown>;
  teamCode?: string;
  teamName?: string;
};

export type TenantLoginFail = {
  ok: false;
  error: string;
  status?: number;
};

export type TenantLoginTeamPick = {
  ok: false;
  needTeamPick: true;
  error: string;
  candidates: TeamPickCandidate[];
};

export type TenantLoginResult = TenantLoginSuccess | TenantLoginFail | TenantLoginTeamPick;

export function isTenantTeamPick(r: TenantLoginResult): r is TenantLoginTeamPick {
  return r.ok === false && 'needTeamPick' in r && r.needTeamPick === true;
}

export async function performTenantLogin(
  teamCode: string,
  username: string,
  password: string,
  selectedUserId?: string,
  wechatBindToken?: string
): Promise<TenantLoginResult> {
  const payload: Record<string, string> = {
    teamCode: String(teamCode || '').trim(),
    username,
    password
  };
  if (selectedUserId) {
    payload.selectedUserId = selectedUserId;
  }
  if (wechatBindToken) {
    payload.wechatBindToken = wechatBindToken;
  }

  const response = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  const rawText = await response.text();
  let body: Record<string, unknown> | null = null;
  if (rawText && rawText.trimStart().startsWith('{')) {
    try {
      body = JSON.parse(rawText) as Record<string, unknown>;
    } catch {
      body = null;
    }
  }

  if (response.status === 409 && body && body.code === 'TEAM_SELECTION_REQUIRED') {
    const candidates = body.candidates;
    return {
      ok: false,
      needTeamPick: true,
      error: (body.error as string) || '请选择要进入的团队',
      candidates: Array.isArray(candidates) ? (candidates as TeamPickCandidate[]) : []
    };
  }

  if (response.ok) {
    const data = body as {
      token?: string;
      user?: Record<string, unknown>;
      teamCode?: string;
      teamName?: string;
    } | null;
    if (!data?.token || !data?.user) {
      return {
        ok: false,
        error:
          '登录响应异常（可能网关把接口指错了）。请联系管理员检查 Nginx 的 /api 代理与后端是否运行。'
      };
    }
    return {
      ok: true,
      token: data.token,
      user: data.user as Record<string, unknown>,
      teamCode: typeof data.teamCode === 'string' ? data.teamCode : undefined,
      teamName: typeof data.teamName === 'string' ? data.teamName : undefined
    };
  }

  let errText = '登录失败，请检查用户名和密码';
  if (body) {
    errText = (body.error as string) || (body.message as string) || errText;
  } else if (response.status === 502 || response.status === 503 || response.status === 504) {
    errText =
      '网关错误（502/503/504）：后端服务未启动或 Nginx 无法连接本机端口。请检查 /api 的 proxy_pass。';
  } else {
    errText = `登录失败（HTTP ${response.status}）`;
  }
  return { ok: false, error: errText, status: response.status };
}
